import { useCallback, useEffect, useRef, useState } from "react";

import { HttpApiService } from "@/shared/services/httpApi.service";

import { PaymentIncidentsService } from "../services/paymentIncidents.service";
import type { PaymentIncidentsSummary } from "../types/paymentIncidents.types";

export interface UsePaymentIncidentsSummaryResult {
  /** The last summary read; after a failed read it is the previous one, and `error` says so. */
  summary: PaymentIncidentsSummary | null;
  isLoading: boolean;
  /** The last read failed. With a `summary`, its figures are out of date (as of `summary.generated_at`). */
  error: string | null;
  retry: () => void;
}

/** The incidents at a glance, once `enabled`; read again whenever `reloadSignal` moves. Last answer wins. */
export function usePaymentIncidentsSummary(
  enabled: boolean,
  reloadSignal: number,
): UsePaymentIncidentsSummaryResult {
  const [retryKey, setRetryKey] = useState(0);
  const key = `${reloadSignal}|${retryKey}`;
  const [state, setState] = useState<{
    key: string | null;
    summary: PaymentIncidentsSummary | null;
    error: string | null;
  }>({ key: null, summary: null, error: null });
  const seq = useRef(0);

  useEffect(() => {
    if (!enabled) return;
    const current = ++seq.current;
    PaymentIncidentsService.summary()
      .then((summary) => {
        if (current === seq.current) setState({ key, summary, error: null });
      })
      .catch((error: unknown) => {
        if (current !== seq.current) return;
        // The previous figures stay on screen, flagged by `error`: a blank strip would hide what was known.
        setState((previous) => ({
          key,
          summary: previous.summary,
          error: HttpApiService.getErrorMessage(error),
        }));
      });
  }, [enabled, key]);

  const retry = useCallback(() => setRetryKey((value) => value + 1), []);
  return { summary: state.summary, isLoading: enabled && state.key !== key, error: state.error, retry };
}
